import { settings } from '@/main/config/settings'
import { createContext, ReactNode, useCallback, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { setRecaptchaToken } from './recaptcha-token-store'

export interface RecaptchaContextType {
  executeRecaptcha: (callback: () => Promise<unknown>) => Promise<void>
}

export const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined)

interface RecaptchaProviderProps {
  children: ReactNode
}

export const RecaptchaProvider: React.FC<RecaptchaProviderProps> = ({ children }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => Promise<unknown>) | null>(null)

  const executeRecaptcha = useCallback(async (callback: () => Promise<unknown>) => {
    if (!recaptchaRef.current) {
      console.error('reCAPTCHA not ready')
      return
    }
    setOnSuccessCallback(() => callback)

    await recaptchaRef.current.executeAsync()
  }, [])

  const onRecaptchaChange = async (token: string | null) => {
    setRecaptchaToken(token)
    if (token && onSuccessCallback) {
      try {
        await onSuccessCallback()
      } catch (error) {
        console.error('Callback execution failed after getting token', error)
      } finally {
        recaptchaRef.current?.reset()
        setOnSuccessCallback(null)
      }
    }
  }

  return (
    <RecaptchaContext.Provider value={{ executeRecaptcha }}>
      {children}
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={settings.RECAPTCHA_SITE_KEY}
        onChange={onRecaptchaChange}
      />
    </RecaptchaContext.Provider>
  )
}
